import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Save, Trash2, Sparkles } from "lucide-react";
import SmartBriefBuilder from "@/components/SmartBriefBuilder";

const FIELDS = [
    ["company_name", "Company Name", "input", true],
    ["industry", "Industry", "input"],
    ["website", "Website URL", "input"],
    ["instagram", "Instagram URL", "input"],
    ["facebook", "Facebook URL", "input"],
    ["youtube", "YouTube URL", "input"],
    ["linkedin", "LinkedIn URL", "input"],
    // Sprint B — top-up social columns (CEO intake can pre-fill these).
    ["x_twitter", "X / Twitter URL", "input"],
    ["google_business_profile", "Google Business Profile URL", "input"],
    ["geography", "Geography", "input"],
    ["product_service", "Product / Service", "textarea"],
    ["target_audience", "Target Audience", "textarea"],
    ["competitors", "Competitors", "textarea"],
    ["vision", "Vision", "textarea"],
    ["mission", "Mission", "textarea"],
    ["brand_story", "Brand Story (includes Founder Story when extracted)", "textarea"],
    ["tone_of_voice", "Tone of Voice", "textarea"],
    ["brand_colors", "Brand Colors", "input"],
    ["fonts", "Fonts", "input"],
    ["brand_guidelines", "Brand Guidelines", "textarea"],
    ["current_offers", "Current Offers", "textarea"],
    ["campaign_goals", "Campaign Goals", "textarea"],
    ["mandatory_elements", "Mandatory Elements", "textarea"],
    ["things_to_avoid", "Things to Avoid", "textarea"],
];

const empty = Object.fromEntries(FIELDS.map(([k]) => [k, ""]));

export default function ClientForm() {
    const { id } = useParams();
    const isEdit = id && id !== "new";
    const nav = useNavigate();
    const [form, setForm] = useState(empty);
    const [busy, setBusy] = useState(false);
    const [showCeoIntake, setShowCeoIntake] = useState(false);
    const [autoFilled, setAutoFilled] = useState(new Set());

    useEffect(() => {
        if (!isEdit) return;
        api.get(`/clients/${id}`).then(({ data }) => setForm({ ...empty, ...data }));
    }, [id, isEdit]);

    const update = (k, v) => setForm(f => ({ ...f, [k]: v }));

    const save = async () => {
        if (!form.company_name) { toast.error("Company name is required"); return; }
        setBusy(true);
        try {
            const payload = { ...form };
            const r = isEdit
                ? await api.put(`/clients/${id}`, payload)
                : await api.post("/clients", payload);
            toast.success(isEdit ? "Client updated." : "Client created.");
            nav(`/clients/${r.data.id}`);
        } catch (e) {
            if (e?.response?.status !== 402) {
                toast.error(e?.userMessage || "Save failed");
            }
        } finally { setBusy(false); }
    };

    const remove = async () => {
        if (!isEdit) return;
        if (!window.confirm("Delete this client and ALL its projects? This cannot be undone.")) return;
        await api.delete(`/clients/${id}`);
        toast.success("Client removed.");
        nav("/clients");
    };

    // Sprint B — Apply a confirmed CEO intake draft into the form. We
    // only touch fields the user explicitly confirmed; everything else
    // is left untouched. Founder story is folded into brand_story with
    // a "Founder Story:" heading.
    const handleApplyDraft = (draft) => {
        const cli = draft?.client || {};
        const filled = new Set(autoFilled);
        setForm((prev) => {
            const next = { ...prev };
            // 1) Direct field copies.
            Object.entries(cli).forEach(([k, v]) => {
                if (v === undefined || v === null || v === "") return;
                if (!(k in empty)) return;        // skip schema-foreign keys
                if (k === "brand_story") return;  // handled separately below
                if (k === "founder_story") return;
                next[k] = v;
                filled.add(k);
            });
            // 2) Founder story → brand_story with heading.
            const founder = (cli.founder_story || "").trim();
            const story = (cli.brand_story || "").trim();
            const merged = [story, founder ? `Founder Story:\n${founder}` : ""]
                .filter(Boolean).join("\n\n");
            if (merged && !next.brand_story?.includes("Founder Story:")) {
                next.brand_story = merged;
                filled.add("brand_story");
            } else if (story && !next.brand_story) {
                next.brand_story = story;
                filled.add("brand_story");
            }
            // 3) Competitors → flat string into the textarea (existing
            //    surface). The structured per-project competitor list
            //    only applies once a project is created.
            if (Array.isArray(draft?.competitors) && draft.competitors.length > 0) {
                const names = draft.competitors.map((c) => c?.name).filter(Boolean);
                if (names.length > 0) {
                    next.competitors = Array.from(new Set(
                        [...((prev.competitors || "").split(/[,;\n]/).map((s) => s.trim()).filter(Boolean)), ...names]
                    )).join(", ");
                    filled.add("competitors");
                }
            }
            // 4) Confirmed social links → fill matching client social columns.
            if (Array.isArray(draft?.social_links)) {
                draft.social_links.forEach((s) => {
                    if (!s || s.entity_type !== "client") return;
                    const col = ({
                        instagram: "instagram", facebook: "facebook",
                        linkedin: "linkedin", youtube: "youtube",
                        x_twitter: "x_twitter",
                        google_business_profile: "google_business_profile",
                    })[s.platform];
                    if (col && s.url && !next[col]) {
                        next[col] = s.url;
                        filled.add(col);
                    }
                });
            }
            return next;
        });
        setAutoFilled(filled);
        // Scroll the form into view so user can review.
        setTimeout(() => {
            document.getElementById("client-form-body")?.scrollIntoView({
                behavior: "smooth", block: "start",
            });
        }, 100);
    };

    return (
        <div className="p-6 sm:p-8 lg:p-10 max-w-4xl mx-auto" data-testid="client-form-page">
            <div className="mb-8">
                <div className="label-micro mb-2">{isEdit ? "Edit" : "New"} Client</div>
                <h1 className="font-display text-4xl font-medium tracking-tight">{isEdit ? form.company_name || "Client" : "Add client"}</h1>
                <p className="text-zinc-500 text-sm mt-2">Every agent reads this profile before generating.</p>
            </div>

            {/* Sprint B — Ask CEO to help fill client details */}
            {!showCeoIntake ? (
                <div className="mb-6 card-base p-5 ceo-glow flex items-center justify-between gap-4 flex-wrap">
                    <div>
                        <div className="label-micro mb-1">CEO smart intake</div>
                        <div className="text-sm text-zinc-200">
                            Paste website/company/LinkedIn/founder story and let the CEO pre-fill this form.
                        </div>
                        <div className="text-[10px] text-zinc-500 mt-1">
                            Every field still needs your Confirm before it lands here. You can edit anything.
                        </div>
                    </div>
                    <button
                        data-testid="client-ceo-open"
                        onClick={() => setShowCeoIntake(true)}
                        className="btn-primary text-sm"
                    >
                        <Sparkles className="w-4 h-4" /> Ask CEO to help fill client details
                    </button>
                </div>
            ) : (
                <SmartBriefBuilder
                    context="client"
                    targetClientId={isEdit ? id : null}
                    title="Paste your client's website, LinkedIn, or founder story."
                    onApply={handleApplyDraft}
                />
            )}

            <div id="client-form-body" className="card-base p-6 grid grid-cols-1 md:grid-cols-2 gap-5">
                {FIELDS.map(([key, label, type, req]) => (
                    <div key={key} className={type === "textarea" ? "md:col-span-2" : ""}>
                        <label className="label-micro block mb-2 flex items-center gap-2">
                            <span>{label}{req && <span className="text-[#FF4400]"> *</span>}</span>
                            {autoFilled.has(key) && (
                                <span className="text-[9px] uppercase tracking-wider text-orange-400 border border-orange-500/40 px-1.5 py-0.5 rounded-sm">
                                    CEO filled
                                </span>
                            )}
                        </label>
                        {type === "textarea" ? (
                            <textarea
                                data-testid={`client-${key}`}
                                rows={3}
                                value={form[key] || ""} onChange={e => update(key, e.target.value)}
                                className={`w-full bg-black border rounded-sm px-3 py-2.5 outline-none resize-y ${
                                    autoFilled.has(key)
                                        ? "border-orange-500/50 focus:border-orange-400"
                                        : "border-zinc-800 focus:border-zinc-500"
                                }`}
                            />
                        ) : (
                            <input
                                data-testid={`client-${key}`}
                                value={form[key] || ""} onChange={e => update(key, e.target.value)}
                                className={`w-full bg-black border rounded-sm px-3 py-2.5 outline-none ${
                                    autoFilled.has(key)
                                        ? "border-orange-500/50 focus:border-orange-400"
                                        : "border-zinc-800 focus:border-zinc-500"
                                }`}
                            />
                        )}
                    </div>
                ))}
            </div>

            <div className="flex items-center justify-between mt-6">
                {isEdit ? (
                    <button data-testid="client-delete" onClick={remove} className="btn-danger">
                        <Trash2 className="w-4 h-4" /> Delete client
                    </button>
                ) : <span />}
                <button data-testid="client-save" onClick={save} disabled={busy} className="btn-primary">
                    <Save className="w-4 h-4" /> {busy ? "Saving…" : "Save client"}
                </button>
            </div>
        </div>
    );
}
